import BackendService from './BackendService';
import {
  CreateTaskRequest,
  UpdateTaskRequest,
  UpdateStatusRequest,
  TasksResponse,
  TaskResponse,
  UsersResponse,
  CreateChecklistRequest,
  UpdateChecklistRequest,
  ChecklistsResponse,
  ChecklistResponse,
  UpdateTaskChecklistRequest,
} from '../../types/ContentTracking';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

const ContentTrackingService = {
  // Get all users for assignment
  getUsers: async (): Promise<UsersResponse> => {
    const response = await BackendService.Get(`${BASE_URL}/auth/admin/users`);
    // Handle direct array response (not wrapped in status/data object)
    if (Array.isArray(response.data)) {
      return {
        status: true,
        message: 'Users fetched successfully',
        data: response.data,
      };
    }
    return response.data;
  },

  // Create a new task (Super Admin only)
  createTask: async (taskData: CreateTaskRequest): Promise<TaskResponse> => {
    const response = await BackendService.Post(`${BASE_URL}/content-tracking/tasks`, taskData);
    return response.data;
  },

  // Get all tasks
  getAllTasks: async (
    page = 1,
    limit = 10,
    userId?: string,
    period?: string
  ): Promise<TasksResponse> => {
    let url = `${BASE_URL}/content-tracking/tasks?page=${page}&limit=${limit}`;
    if (userId) {
      url += `&userId=${userId}`;
    }
    if (period && period !== 'all') {
      url += `&period=${period}`;
    }
    const response = await BackendService.Get(url);
    return response.data;
  },

  // Update a task (Admin only)
  updateTask: async (taskId: string, taskData: UpdateTaskRequest): Promise<TaskResponse> => {
    const response = await BackendService.Patch(
      `${BASE_URL}/content-tracking/tasks/${taskId}`,
      taskData
    );
    return response.data;
  },

  // Update task status (Admin only)
  updateTaskStatus: async (
    taskId: string,
    statusData: UpdateStatusRequest
  ): Promise<TaskResponse> => {
    const response = await BackendService.Put(
      `${BASE_URL}/content-tracking/tasks/${taskId}/status`,
      statusData
    );
    return response.data;
  },

  // Delete a task (Admin only)
  deleteTask: async (taskId: string): Promise<{ message: string; status: boolean }> => {
    const response = await BackendService.Delete(`${BASE_URL}/content-tracking/tasks/${taskId}`);
    return response.data;
  },

  // Update checklist (Admin only)
  updateChecklist: async (
    taskId: string,
    checklistData: UpdateTaskChecklistRequest
  ): Promise<TaskResponse> => {
    const response = await BackendService.Put(
      `${BASE_URL}/content-tracking/tasks/${taskId}/checklist`,
      checklistData
    );
    return response.data;
  },

  // Update single checklist item (PATCH - more efficient for single item updates)
  updateChecklistItemStatus: async (
    taskId: string,
    checklistItemId: string,
    isCompleted: boolean,
    completedAt?: string
  ): Promise<TaskResponse> => {
    const response = await BackendService.Patch(
      `${BASE_URL}/content-tracking/tasks/${taskId}/checklist/${checklistItemId}`,
      {
        isCompleted,
        completedAt: completedAt || (isCompleted ? new Date().toISOString() : undefined),
      }
    );
    return response.data;
  },

  // New checklist management APIs
  // Create a new checklist (Super Admin only)
  createChecklist: async (checklistData: CreateChecklistRequest): Promise<ChecklistResponse> => {
    const response = await BackendService.Post(`${BASE_URL}/checklist/create`, checklistData);
    return response.data;
  },

  // Get all checklists
  getAllChecklists: async (page = 1, limit = 10): Promise<ChecklistsResponse> => {
    const response = await BackendService.Get(
      `${BASE_URL}/checklist/all?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Get active checklists by category
  getActiveChecklistsByCategory: async (
    category: string,
    page = 1,
    limit = 10
  ): Promise<ChecklistsResponse> => {
    const response = await BackendService.Get(
      `${BASE_URL}/checklist/active/category/${category}?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Update a checklist (Super Admin only)
  updateChecklistItem: async (
    checklistId: string,
    checklistData: UpdateChecklistRequest
  ): Promise<ChecklistResponse> => {
    const response = await BackendService.Put(
      `${BASE_URL}/checklist/${checklistId}`,
      checklistData
    );
    return response.data;
  },

  // Delete a checklist (Super Admin only)
  deleteChecklist: async (checklistId: string): Promise<{ message: string; status: boolean }> => {
    const response = await BackendService.Delete(`${BASE_URL}/checklist/${checklistId}`);
    return response.data;
  },

  // Reorder checklists (Admin/Superadmin only)
  reorderChecklists: async (
    orderedIds: string[]
  ): Promise<{ message: string; status: boolean }> => {
    const response = await BackendService.Put(`${BASE_URL}/checklist/reorder`, { orderedIds });
    return response.data;
  },

  // Request approval for a task
  requestApproval: async (
    taskId: string,
    data: { seoImage: File; driveUrl: string }
  ): Promise<TaskResponse> => {
    const formData = new FormData();
    formData.append('seoImage', data.seoImage);
    formData.append('driveUrl', data.driveUrl);

    // Debug FormData contents
    console.log('FormData entries:');
    formData.forEach((value, key) => {
      console.log(key, value);
    });

    const response = await BackendService.PostFormData(
      `${BASE_URL}/content-tracking/tasks/${taskId}/request-approval`,
      formData
    );
    return response.data;
  },

  // Approve a task (Super Admin only)
  approveTask: async (taskId: string, isApprove: boolean): Promise<TaskResponse> => {
    const response = await BackendService.Post(
      `${BASE_URL}/content-tracking/tasks/${taskId}/approve`,
      { isApprove }
    );
    return response.data;
  },

  // Update task details (SEO image and Drive URL) using PATCH
  updateTaskDetails: async (
    taskId: string,
    data: { seoImage?: File; driveUrl?: string }
  ): Promise<TaskResponse> => {
    const formData = new FormData();
    if (data.seoImage) {
      formData.append('seoImage', data.seoImage);
    }
    if (data.driveUrl !== undefined) {
      formData.append('driveUrl', data.driveUrl);
    }

    const response = await BackendService.PatchFormData(
      `${BASE_URL}/content-tracking/tasks/${taskId}/details`,
      formData
    );
    return response.data;
  },

  // Prompt Library APIs
  getAllPrompts: async (page = 1, limit = 10) => {
    const response = await BackendService.Get(`${BASE_URL}/prompts?page=${page}&limit=${limit}`);
    return response.data;
  },

  getPromptsByCategory: async (category: string) => {
    const response = await BackendService.Get(`${BASE_URL}/prompts/category/${category}`);
    return response.data;
  },

  createPrompt: async (data: any) => {
    const response = await BackendService.Post(`${BASE_URL}/prompts`, data);
    return response.data;
  },

  updatePrompt: async (id: string, data: any) => {
    const response = await BackendService.Put(`${BASE_URL}/prompts/${id}`, data);
    return response.data;
  },

  deletePrompt: async (id: string) => {
    const response = await BackendService.Delete(`${BASE_URL}/prompts/${id}`);
    return response.data;
  },

  // Prompt Category APIs
  getAllCategories: async () => {
    const response = await BackendService.Get(`${BASE_URL}/prompts/categories`);
    return response.data;
  },

  createCategory: async (data: { name: string; description?: string }) => {
    const response = await BackendService.Post(`${BASE_URL}/prompts/categories`, data);
    return response.data;
  },

  deleteCategory: async (id: string) => {
    const response = await BackendService.Delete(`${BASE_URL}/prompts/categories/${id}`);
    return response.data;
  },
};

export default ContentTrackingService;
